import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Address } from 'viem';
import { faWarning } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { Input, InputWrapper } from '@components/basics/forms/inputs/Input';
import { useAuth } from '@context/auth/base';
import { ParamsStore, useParams } from '@context/params';
import { useValidateWalletOrEns } from '@hooks/useValidateWalletOrEns';

const ADDRESS_WARNING =
  'Items sent to the wrong address cannot be recovered. Be sure this is the correct wallet address.';
const MAX_ERROR_MESSAGE =
  'This address has already reached the maximum number of editions allowed per wallet.';

export const GiftingAddressInput = ({
  inputDisabled = false,
  setGiftAddress,
  setOpen,
  eligibleQuantity,
  inputTouched,
  setInputTouched,
  inputValue,
  setInputValue,
}: {
  inputDisabled: boolean;
  setGiftAddress: React.Dispatch<React.SetStateAction<Address | null>>;
  setOpen: ((open: boolean) => void) | null;
  eligibleQuantity: number;
  inputTouched: boolean;
  setInputTouched: (touched: boolean) => void;
  inputValue: string | null;
  setInputValue: (value: string | null) => void;
}) => {
  const { authUserAddress } = useAuth();

  const { data, isLoading } = useValidateWalletOrEns({
    input: inputValue,
  });

  const { gift_to } = useParams();

  const validatedData = React.useMemo(() => {
    if (!data || !inputTouched) return null;

    if (data.type === 'address') {
      if (data.value === authUserAddress) {
        return {
          type: 'auth-address',
          value: 'The address you entered is you own address.',
        };
      } else {
        setGiftAddress(data.value);
      }

      return data;
    } else {
      setGiftAddress(null);
      return {
        type: data.type,
        value: getErrorMessage({ type: data.type }),
      };
    }
  }, [authUserAddress, data, inputTouched, setGiftAddress]);

  const maxEligibleError = eligibleQuantity < 1;

  const onInputChange = (value: string | null) => {
    setInputValue(value);
    setInputTouched(true);
  };

  const hasError = React.useMemo(
    () =>
      ((!!validatedData && validatedData.type !== 'address') || maxEligibleError) && inputTouched,
    [validatedData, maxEligibleError, inputTouched],
  );

  React.useEffect(() => {
    if (maxEligibleError) {
      ParamsStore.gift_to = null;
      return;
    }

    if (!!validatedData && validatedData.type === 'address') {
      ParamsStore.gift_to = validatedData?.value;
      return;
    }
  }, [maxEligibleError, validatedData]);

  React.useEffect(() => {
    if (!!gift_to) {
      setOpen?.(true);
      setGiftAddress(gift_to);
      if (!inputTouched) {
        setInputValue(gift_to);
      }
    }
  }, [gift_to, inputTouched, setGiftAddress, setInputValue, setOpen]);

  React.useEffect(() => {
    if (!validatedData || !inputTouched) return;

    if (validatedData.type === 'address') {
      ParamsStore.gift_to = validatedData.value;
    } else {
      ParamsStore.gift_to = null;
    }
  }, [inputTouched, validatedData]);

  return (
    <div>
      <InputWrapper error={hasError}>
        <Input
          type="text"
          value={!!inputValue ? inputValue : ''}
          placeholder="e.g. 0xB0A3... or wallet.eth"
          aria-label="wallet address input field"
          autoComplete="off"
          disabled={inputDisabled}
          onChange={e => {
            onInputChange(e.target.value);
          }}
          onBlur={e => {
            onInputChange(e.target.value);
            if (inputTouched && e.target.value === '') {
              setOpen?.(false);
              setInputTouched(false);
              setInputValue(null);
              setGiftAddress(null);
            }
          }}
        />
        {inputTouched && (
          <InputWrapper.Error
            errorMessage={maxEligibleError ? MAX_ERROR_MESSAGE : validatedData?.value ?? null}
          />
        )}
      </InputWrapper>
      {!isLoading && !hasError && !!gift_to && (
        <div className="mt-1 flex w-full items-start gap-2">
          <FontAwesomeIcon icon={faWarning} className="text-warning400" fontSize={14} />
          <p className="font-base text-base-s text-base500">{ADDRESS_WARNING}</p>
        </div>
      )}
    </div>
  );
};

function getErrorMessage({
  type,
}: {
  type: NonNullable<ReturnType<typeof useValidateWalletOrEns>['data']>['type'];
}) {
  switch (type) {
    case 'address':
      return null;
    case 'invalid-address':
      return 'Please enter a valid address';
    case 'invalid-ens':
      return 'Please enter a valid ENS name';
    case 'empty-input':
      return 'Please enter an address or ENS name';
    default:
      return null;
  }
}
